import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { Slider } from 'primeng/slider';
import { AnimationStateService } from '../../util/animation-state.service';
import { PositionWithColor } from '../../util/position-with-color';

/** Number of bars in the density strip behind the time-range slider. */
const DENSITY_BUCKETS = 96;

interface DensityBar {
  /** 0..1 share of the strip width. */
  x: number;
  /** 0..1 relative height (max bucket = 1). */
  h: number;
  color: string;
}

/**
 * Replay console of the position-history map: transport (play / pause / stop), speed, a time
 * range slider drawn over the position density of the loaded period, and the "show points"
 * toggle. Sits at the bottom of the map; the map itself handles point selection.
 */
@Component({
  selector: 'lib-map-read-controls',
  standalone: true,
  imports: [DatePipe, FormsModule, Slider, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section
      class="pointer-events-auto w-[min(56rem,calc(100vw-1.5rem))] rounded-2xl border border-black/5 bg-white/95 text-gray-900 shadow-xl backdrop-blur-md dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-100"
      [attr.aria-label]="'positionReplay.title' | transloco">
      <div class="flex items-center gap-3 px-3 pt-3 sm:px-4">
        <!-- Transport -->
        <div class="flex items-center gap-1.5">
          <button
            type="button"
            class="bg-primary-500 hover:bg-primary-600 focus-visible:ring-primary-300 inline-flex size-10 items-center justify-center rounded-full text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-40"
            [disabled]="positionsWithColor().length < 2"
            [attr.aria-label]="
              (animationState() === 'playing' ? 'positionReplay.pause' : 'positionReplay.play') | transloco
            "
            [title]="(animationState() === 'playing' ? 'positionReplay.pause' : 'positionReplay.play') | transloco"
            (click)="onPlayPause()">
            <i
              class="pi"
              [class.pi-pause]="animationState() === 'playing'"
              [class.pi-play]="animationState() !== 'playing'"></i>
          </button>
          <button
            type="button"
            class="inline-flex size-9 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 disabled:opacity-30 dark:text-slate-300 dark:hover:bg-slate-800 dark:focus-visible:ring-slate-600"
            [disabled]="animationState() === 'stopped'"
            [attr.aria-label]="'positionReplay.stop' | transloco"
            [title]="'positionReplay.stop' | transloco"
            (click)="onStop()">
            <i class="pi pi-stop"></i>
          </button>
        </div>

        <!-- Position in the replay -->
        <div class="min-w-0 flex-1 text-sm">
          @if (animationState() !== 'stopped' && playheadTime(); as t) {
            <span class="font-semibold tabular-nums">{{ t | date: 'dd/MM HH:mm:ss' }}</span>
            <span class="text-gray-500 dark:text-slate-400">
              · {{ playheadIndex() + 1 }} / {{ positionsWithColor().length }}
            </span>
          } @else {
            <span class="text-gray-500 dark:text-slate-400">
              {{
                'positionReplay.inRange' | transloco: { count: previewCount(), total: allPositionsWithColor().length }
              }}
            </span>
          }
        </div>

        <!-- Speed -->
        <div
          class="hidden items-center rounded-full bg-gray-100 p-0.5 text-xs sm:flex dark:bg-slate-800"
          role="radiogroup"
          [attr.aria-label]="'positionReplay.speed' | transloco">
          @for (s of speeds(); track s.value) {
            <button
              type="button"
              role="radio"
              [attr.aria-checked]="s.value === animationsStep()"
              class="focus-visible:ring-primary-300 rounded-full px-2.5 py-1 tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2"
              [class.bg-white]="s.value === animationsStep()"
              [class.shadow-sm]="s.value === animationsStep()"
              [class.font-semibold]="s.value === animationsStep()"
              [class.dark:bg-slate-600]="s.value === animationsStep()"
              [class.text-gray-500]="s.value !== animationsStep()"
              [class.dark:text-slate-400]="s.value !== animationsStep()"
              (click)="onSetSpeed(s.value)">
              {{ s.label }}
            </button>
          }
        </div>

        <!-- Show points -->
        <label
          class="flex cursor-pointer select-none items-center gap-2 text-xs text-gray-600 dark:text-slate-300"
          [class.opacity-50]="animationState() !== 'stopped'">
          <input
            type="checkbox"
            class="peer sr-only"
            [checked]="showAllPositionsSignal()"
            [disabled]="animationState() !== 'stopped'"
            (change)="onPointsToggle($any($event.target).checked)" />
          <span
            class="peer-focus-visible:ring-primary-300 peer-checked:bg-primary-500 relative inline-block h-5 w-9 rounded-full bg-gray-300 transition-colors peer-focus-visible:ring-2 dark:bg-slate-600"
            aria-hidden="true">
            <span
              class="absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow transition-transform"
              [class.translate-x-4]="showAllPositionsSignal()"></span>
          </span>
          {{ 'positionReplay.points' | transloco }}
        </label>
      </div>

      <!-- Timeline: density strip + range slider + playhead -->
      @if (timeRange(); as range) {
        <div class="px-3 pb-3 pt-2 sm:px-4">
          <div class="relative h-12">
            <svg
              class="absolute inset-x-0 bottom-3 h-8 w-full"
              viewBox="0 0 1000 100"
              preserveAspectRatio="none"
              aria-hidden="true">
              @for (b of density(); track b.x) {
                <rect
                  [attr.x]="b.x * 1000"
                  [attr.y]="100 - b.h * 100"
                  [attr.width]="1000 / bucketCount + 0.5"
                  [attr.height]="b.h * 100"
                  [attr.fill]="b.color"
                  [attr.opacity]="inSelectedRange(b.x) ? 0.9 : 0.25" />
              }
            </svg>
            @if (animationState() !== 'stopped') {
              <div
                class="pointer-events-none absolute bottom-2 top-0 w-0.5 rounded bg-gray-900 dark:bg-white"
                [style.left.%]="playheadPct()"
                aria-hidden="true"></div>
            }
            <p-slider
              class="replay-range absolute inset-x-0 bottom-0 block"
              [min]="0"
              [max]="100"
              [range]="true"
              [step]="0.5"
              [(ngModel)]="timeRangePercent"
              (onSlideEnd)="onTimeRangeChange()">
            </p-slider>
          </div>
          <div class="mt-1 flex items-baseline justify-between text-xs tabular-nums text-gray-500 dark:text-slate-400">
            <span>{{ selectedStartTime() | date: 'dd/MM/yy HH:mm:ss' }}</span>
            <span class="flex items-center gap-2">
              <span class="font-medium text-gray-700 dark:text-slate-200">{{
                formatDuration(selectedDuration())
              }}</span>
              @if (timeRangePercent[0] !== 0 || timeRangePercent[1] !== 100) {
                <button
                  type="button"
                  class="text-primary-600 hover:text-primary-700 dark:text-primary-400 focus-visible:ring-primary-300 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2"
                  (click)="resetTimeFilter()">
                  {{ 'positionReplay.resetRange' | transloco }}
                </button>
              }
            </span>
            <span>{{ selectedEndTime() | date: 'dd/MM/yy HH:mm:ss' }}</span>
          </div>
        </div>
      } @else {
        <p class="px-4 pb-3 pt-2 text-xs text-gray-500 dark:text-slate-400">
          {{ 'positionReplay.tooFewPositions' | transloco }}
        </p>
      }
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      /* The PrimeNG range slider sits on top of the density strip: keep only the handles and a
         thin track so the bars stay readable. */
      .replay-range ::ng-deep .p-slider {
        background: rgba(148, 163, 184, 0.35);
        height: 4px;
        border-radius: 9999px;
      }
      .replay-range ::ng-deep .p-slider-range {
        background: #ed107d;
        border-radius: 9999px;
      }
      .replay-range ::ng-deep .p-slider-handle {
        width: 18px;
        height: 18px;
        margin-top: -9px;
        margin-left: -9px;
        border: 2px solid #ed107d;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
      }
      .replay-range ::ng-deep .p-slider-handle:focus-visible {
        outline: 2px solid #f9a8d4;
        outline-offset: 2px;
      }
    `,
  ],
})
export class MapReadControlsComponent {
  positionsWithColor = input.required<PositionWithColor[]>();
  allPositionsWithColor = input.required<PositionWithColor[]>();
  animationsStep = input.required<number>();
  speeds = input.required<{ label: string; value: number }[]>();
  /** Whether the points layer is shown — owned by the parent, mirrored here. */
  showAllPositionsSignal = input<boolean>(false);
  trackerSelected = output<PositionWithColor>();
  #animationStateService = inject(AnimationStateService);
  animationState = this.#animationStateService.animationState;

  playPause = output<void>();
  animationStop = output<void>();
  setSpeed = output<number>();
  togglePositions = output<boolean>();
  timeRangeChanged = output<{ start: number; end: number }>();

  protected readonly bucketCount = DENSITY_BUCKETS;

  timeRangePercentSignal = signal<[number, number]>([0, 100]);
  get timeRangePercent(): [number, number] {
    return this.timeRangePercentSignal();
  }
  set timeRangePercent(value: [number, number]) {
    this.timeRangePercentSignal.set(value);
  }

  timeRange = computed(() => {
    const positions = this.allPositionsWithColor();
    if (positions.length < 2) return null;
    const timestamps = positions.map(p => (p.timestamp ? new Date(p.timestamp).getTime() : 0)).filter(t => t > 0);
    if (timestamps.length < 2) return null;
    return { minTime: Math.min(...timestamps), maxTime: Math.max(...timestamps) };
  });

  selectedStartTime = computed(() => {
    const range = this.timeRange();
    if (!range) return null;
    const duration = range.maxTime - range.minTime;
    return new Date(range.minTime + (duration * this.timeRangePercentSignal()[0]) / 100);
  });

  selectedEndTime = computed(() => {
    const range = this.timeRange();
    if (!range) return null;
    const duration = range.maxTime - range.minTime;
    return new Date(range.minTime + (duration * this.timeRangePercentSignal()[1]) / 100);
  });

  filteredCount = signal(0);

  totalDuration = computed(() => {
    const range = this.timeRange();
    return range ? range.maxTime - range.minTime : 0;
  });

  selectedDuration = computed(() => {
    const range = this.timeRange();
    const percent = this.timeRangePercentSignal();
    if (!range) return 0;
    return ((range.maxTime - range.minTime) * (percent[1] - percent[0])) / 100;
  });

  /** Estimated count in the selected range (share of the total, no filtering during drag). */
  previewCount = computed(() => {
    const percent = this.timeRangePercentSignal();
    const total = this.allPositionsWithColor().length;
    return Math.round((total * (percent[1] - percent[0])) / 100);
  });

  /**
   * Position density over the loaded period, one bar per bucket, coloured with the same
   * time-hue ramp as the points on the map so the strip doubles as the colour legend.
   */
  density = computed<DensityBar[]>(() => {
    const range = this.timeRange();
    const positions = this.allPositionsWithColor();
    if (!range || positions.length === 0) return [];
    const span = Math.max(1, range.maxTime - range.minTime);
    const counts = new Array<number>(DENSITY_BUCKETS).fill(0);
    const colorAt = new Array<string | undefined>(DENSITY_BUCKETS);
    for (const p of positions) {
      if (!p.timestamp) continue;
      const t = new Date(p.timestamp).getTime();
      const i = Math.min(DENSITY_BUCKETS - 1, Math.floor(((t - range.minTime) / span) * DENSITY_BUCKETS));
      counts[i]++;
      colorAt[i] ??= p.color;
    }
    const max = Math.max(1, ...counts);
    let lastColor = positions[0].color ?? '#94a3b8';
    return counts.map((c, i) => {
      lastColor = colorAt[i] ?? lastColor;
      // Square-root scale keeps sparse buckets visible next to dense ones.
      return { x: i / DENSITY_BUCKETS, h: c === 0 ? 0.04 : Math.sqrt(c / max), color: lastColor };
    });
  });

  /** Index of the point currently replayed (polled while playing — the counter is not a signal). */
  playheadIndex = signal(0);
  playheadTime = computed(() => {
    const p = this.positionsWithColor()[this.playheadIndex()];
    return p?.timestamp ? new Date(p.timestamp) : null;
  });
  /** Playhead abscissa on the density strip, in % of the loaded period. */
  playheadPct = computed(() => {
    const range = this.timeRange();
    const t = this.playheadTime()?.getTime();
    if (!range || t == null) return 0;
    return ((t - range.minTime) / Math.max(1, range.maxTime - range.minTime)) * 100;
  });

  #destroyRef = inject(DestroyRef);

  constructor() {
    let timer: ReturnType<typeof setInterval> | undefined;
    effect(
      onCleanup => {
        const state = this.animationState();
        if (timer) clearInterval(timer);
        if (state === 'stopped') {
          this.playheadIndex.set(0);
          return;
        }
        const tick = () =>
          this.playheadIndex.set(
            Math.min(Math.max(0, this.#animationStateService.currentCounter - 1), this.positionsWithColor().length - 1)
          );
        tick();
        timer = setInterval(tick, 100);
        onCleanup(() => clearInterval(timer));
      },
      { allowSignalWrites: true }
    );
    this.#destroyRef.onDestroy(() => timer && clearInterval(timer));
  }

  protected inSelectedRange(x: number): boolean {
    const [a, b] = this.timeRangePercentSignal();
    const pct = x * 100;
    return pct >= a && pct <= b;
  }

  onPlayPause() {
    this.playPause.emit();
  }

  onStop() {
    // The parent restores the points layer itself once the animation reports completion.
    this.animationStop.emit();
  }

  onSetSpeed(speed: number) {
    this.setSpeed.emit(speed);
  }

  onTrackerSelect(position: PositionWithColor): void {
    this.trackerSelected.emit(position);
  }

  onPointsToggle(checked: boolean) {
    this.togglePositions.emit(checked);
  }

  onTimeRangeChange() {
    const range = this.timeRange();
    const percent = this.timeRangePercentSignal();
    if (!range) return;
    const duration = range.maxTime - range.minTime;
    this.timeRangeChanged.emit({
      start: range.minTime + (duration * percent[0]) / 100,
      end: range.minTime + (duration * percent[1]) / 100,
    });
  }

  resetTimeFilter() {
    this.timeRangePercentSignal.set([0, 100]);
    this.onTimeRangeChange();
  }

  updateFilteredCount(count: number) {
    this.filteredCount.set(count);
  }

  formatDuration(ms: number): string {
    if (ms <= 0) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) {
      const remainingHours = hours % 24;
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    }
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }
    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }
    return `${seconds}s`;
  }
}
